import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Euro,
  Package,
  Settings,
  Filter,
  RefreshCw,
  Save,
  X,
  Check,
  Upload,
  FileText,
  AlertCircle
} from 'lucide-react';

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PriceList = ({ isOpen, onClose }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateActions, setDuplicateActions] = useState({});
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    item_number: '',
    name: '',
    description: '',
    item_type: 'material',
    unit_price: '',
    unit: 'Stück',
    category: '',
    notes: ''
  });

  const itemTypes = [
    { value: 'material', label: 'Material', icon: '📦' },
    { value: 'dienstleistung', label: 'Dienstleistung', icon: '🔧' },
    { value: 'geraet', label: 'Gerät', icon: '⚙️' },
    { value: 'sonstiges', label: 'Sonstiges', icon: '📋' }
  ];

  const defaultCategories = [
    'Elektroinstallation',
    'Kabelverlegung',
    'Messungen',
    'Reparaturen',
    'Wartung',
    'Montage',
    'Demontage',
    'Materialien',
    'Werkzeuge',
    'Fahrtkosten'
  ];

  useEffect(() => {
    if (isOpen) {
      fetchItems();
      fetchCategories();
    }
  }, [isOpen, searchTerm, selectedCategory, selectedType]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedType && selectedType !== 'all') params.append('item_type', selectedType);
      
      const response = await axios.get(`${API}/price-list?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching price list:', error);
      toast.error('Fehler beim Laden der Preisliste');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/price-list/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const dbCategories = response.data.categories || [];
      // Combine default categories with existing ones
      const allCategories = [...new Set([...defaultCategories, ...dbCategories])];
      setCategories(allCategories.sort());
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(defaultCategories);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('authToken');
      const submitData = {
        ...formData,
        unit_price: parseFloat(formData.unit_price)
      };

      await axios.post(`${API}/price-list`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Artikel erfolgreich erstellt');
      setShowAddDialog(false);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('Error creating item:', error);
      const message = error.response?.data?.detail || 'Fehler beim Erstellen des Artikels';
      toast.error(message);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditValues({
      item_number: item.item_number,
      name: item.name,
      description: item.description || '',
      unit_price: item.unit_price,
      unit: item.unit,
      category: item.category || '',
      notes: item.notes || ''
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      const token = localStorage.getItem('authToken');
      const updateData = {
        ...editValues,
        unit_price: parseFloat(editValues.unit_price)
      };

      await axios.put(`${API}/price-list/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Artikel erfolgreich aktualisiert');
      setEditingId(null);
      setEditValues({});
      fetchItems();
    } catch (error) {
      console.error('Error updating item:', error);
      const message = error.response?.data?.detail || 'Fehler beim Aktualisieren des Artikels';
      toast.error(message);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleDelete = async (id, itemName) => {
    if (!window.confirm(`Möchten Sie den Artikel "${itemName}" wirklich löschen?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API}/price-list/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Artikel erfolgreich gelöscht');
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      const message = error.response?.data?.detail || 'Fehler beim Löschen des Artikels';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setFormData({
      item_number: '',
      name: '',
      description: '',
      item_type: 'material',
      unit_price: '',
      unit: 'Stück',
      category: '',
      notes: ''
    });
  };

  const getTypeIcon = (type) => {
    const typeInfo = itemTypes.find(t => t.value === type);
    return typeInfo?.icon || '📋';
  };

  const getTypeLabel = (type) => {
    const typeInfo = itemTypes.find(t => t.value === type);
    return typeInfo?.label || type;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      toast.error('Bitte wählen Sie eine CSV oder Excel-Datei');
      return;
    }

    setImportFile(file);
    parseImportFile(file);
  };

  const parseImportFile = (file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          toast.error('Die Datei muss mindestens eine Kopfzeile und eine Datenzeile enthalten');
          return;
        }

        // Parse CSV (support both comma and semicolon)
        const separator = text.includes(';') ? ';' : ',';
        const headers = lines[0].split(separator).map(h => h.trim().replace(/['"]/g, ''));
        
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(separator).map(v => v.trim().replace(/['"]/g, ''));
          
          if (values.length !== headers.length || values.every(v => !v)) continue;
          
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index];
          });
          data.push(row);
        }

        // Map CSV columns to our model
        const mappedData = data.map(row => {
          const itemNumber = row['Artikelnummer'] || row['artikelnummer'] || row['item_number'] || '';
          const name = row['Bezeichnung'] || row['bezeichnung'] || row['name'] || '';
          const price = parseFloat((row['Einzelpreis'] || row['einzelpreis'] || row['unit_price'] || '0').replace(',', '.'));
          
          return {
            item_number: itemNumber,
            name: name,
            description: row['Beschreibung'] || row['beschreibung'] || row['description'] || '',
            item_type: (row['Typ'] || row['typ'] || row['item_type'] || 'material').toLowerCase(),
            unit_price: isNaN(price) ? 0 : price,
            unit: row['Einheit'] || row['einheit'] || row['unit'] || 'Stück',
            category: row['Kategorie'] || row['kategorie'] || row['category'] || '',
            notes: row['Notizen'] || row['notizen'] || row['notes'] || ''
          };
        }).filter(item => item.item_number && item.name);

        if (mappedData.length === 0) {
          toast.error('Keine gültigen Daten gefunden. Stellen Sie sicher, dass die Spalten korrekt benannt sind.');
          return;
        }

        setImportPreview(mappedData);
        checkDuplicates(mappedData);
        
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error('Fehler beim Lesen der Datei: ' + error.message);
      }
    };

    reader.readAsText(file);
  };

  const checkDuplicates = async (data) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/price-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const existingItems = response.data;
      const existingNumbers = new Set(existingItems.map(item => item.item_number));
      
      const dupes = data.filter(item => existingNumbers.has(item.item_number));
      setDuplicates(dupes);
      
      // Initialize duplicate actions (default: skip)
      const actions = {};
      dupes.forEach(item => {
        actions[item.item_number] = 'skip';
      });
      setDuplicateActions(actions);
      
      if (dupes.length > 0) {
        toast.warning(`${dupes.length} Artikel existieren bereits. Bitte wählen Sie eine Aktion.`);
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
      toast.error('Fehler beim Prüfen der Duplikate');
    }
  };

  const handleImport = async () => {
    if (importPreview.length === 0) {
      toast.error('Keine Daten zum Importieren');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      let successCount = 0;
      let skipCount = 0;
      let errorCount = 0;

      for (const item of importPreview) {
        const isDuplicate = duplicates.some(d => d.item_number === item.item_number);
        
        if (isDuplicate) {
          const action = duplicateActions[item.item_number];
          
          if (action === 'skip') {
            skipCount++;
            continue;
          } else if (action === 'overwrite') {
            // Find existing item and update
            try {
              const existingResponse = await axios.get(`${API}/price-list?search=${item.item_number}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              if (existingResponse.data.length > 0) {
                const existingItem = existingResponse.data[0];
                await axios.put(`${API}/price-list/${existingItem.id}`, item, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                successCount++;
              }
            } catch (error) {
              console.error('Error updating item:', error);
              errorCount++;
            }
            continue;
          }
        }

        // Create new item
        try {
          await axios.post(`${API}/price-list`, item, {
            headers: { Authorization: `Bearer ${token}` }
          });
          successCount++;
        } catch (error) {
          console.error('Error creating item:', error);
          errorCount++;
        }
      }

      toast.success(`Import abgeschlossen: ${successCount} erstellt, ${skipCount} übersprungen, ${errorCount} Fehler`);
      
      // Reset import state
      setShowImportDialog(false);
      setImportFile(null);
      setImportPreview([]);
      setDuplicates([]);
      setDuplicateActions({});
      
      // Refresh list
      fetchItems();
      
    } catch (error) {
      console.error('Error during import:', error);
      toast.error('Fehler beim Import');
    }
  };

  const downloadTemplate = () => {
    const headers = ['Artikelnummer', 'Bezeichnung', 'Beschreibung', 'Typ', 'Einzelpreis', 'Einheit', 'Kategorie', 'Notizen'];
    const example = ['MAT-001', 'Beispiel Artikel', 'Dies ist eine Beispielbeschreibung', 'material', '10.50', 'Stück', 'Elektroinstallation', 'Beispielnotiz'];
    
    const csv = [
      headers.join(';'),
      example.join(';')
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'preisliste_vorlage.csv';
    link.click();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Euro className="w-6 h-6 text-emerald-600" />
            Admin Preisliste
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col h-full">
          {/* Header Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            {/* Search and Filters */}
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Suche nach Artikelnummer, Name oder Beschreibung..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Typ wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Typen</SelectItem>
                  {itemTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button onClick={fetchItems} variant="outline" size="sm" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Aktualisieren
              </Button>

              <Button 
                onClick={() => setShowImportDialog(true)} 
                variant="outline" 
                size="sm" 
                className="gap-2 border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                <Upload className="w-4 h-4" />
                Import
              </Button>
              
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4" />
                    Artikel hinzufügen
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Neuen Artikel hinzufügen</DialogTitle>
                  </DialogHeader>
                  <AddItemForm
                    formData={formData}
                    setFormData={setFormData}
                    onSubmit={handleSubmit}
                    categories={categories}
                    itemTypes={itemTypes}
                  />
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Price List Table */}
          <div className="flex-1 overflow-auto border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                <span className="ml-3 text-slate-600">Lade Preisliste...</span>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <Package className="w-12 h-12 mb-4" />
                <p className="text-lg font-medium">Keine Artikel gefunden</p>
                <p className="text-sm">Fügen Sie Artikel hinzu oder ändern Sie die Filter</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-700">Typ</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-700">Artikelnummer</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-700">Bezeichnung</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-700">Kategorie</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-700">Einzelpreis</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-700">Einheit</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-slate-700">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <PriceListRow
                      key={item.id}
                      item={item}
                      index={index}
                      isEditing={editingId === item.id}
                      editValues={editValues}
                      setEditValues={setEditValues}
                      onEdit={handleEdit}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={handleCancelEdit}
                      onDelete={handleDelete}
                      formatPrice={formatPrice}
                      getTypeIcon={getTypeIcon}
                      getTypeLabel={getTypeLabel}
                      categories={categories}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Statistics Footer */}
          <div className="mt-4 pt-4 border-t bg-slate-50 px-4 py-3 rounded-lg">
            <div className="flex justify-between items-center text-sm text-slate-600">
              <span>Gesamt: {items.length} Artikel</span>
              <span>
                Durchschnittspreis: {items.length > 0 ? formatPrice(
                  items.reduce((sum, item) => sum + item.unit_price, 0) / items.length
                ) : '€0,00'}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Preisliste importieren
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto max-h-[70vh]">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Anleitung
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Unterstützte Formate: CSV (mit Semikolon oder Komma)</li>
                <li>• Erforderliche Spalten: Artikelnummer, Bezeichnung, Einzelpreis</li>
                <li>• Optionale Spalten: Beschreibung, Typ, Einheit, Kategorie, Notizen</li>
                <li>• Typ-Werte: material, dienstleistung, geraet, sonstiges</li>
              </ul>
              <Button 
                onClick={downloadTemplate} 
                variant="outline" 
                size="sm" 
                className="mt-3 gap-2"
              >
                <FileText className="w-4 h-4" />
                Vorlage herunterladen
              </Button>
            </div>

            {/* File Upload */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full gap-2 border-dashed border-2 h-24"
              >
                <Upload className="w-6 h-6" />
                <div className="text-center">
                  <div className="font-medium">Datei auswählen</div>
                  <div className="text-sm text-slate-500">CSV oder Excel (.xlsx)</div>
                </div>
              </Button>
              {importFile && (
                <div className="mt-2 text-sm text-slate-600">
                  Ausgewählt: <strong>{importFile.name}</strong>
                </div>
              )}
            </div>

            {/* Preview */}
            {importPreview.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Vorschau ({importPreview.length} Artikel)
                  </h3>
                </div>

                {/* Duplicates Warning */}
                {duplicates.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {duplicates.length} Duplikate gefunden
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {duplicates.map((item) => (
                        <div key={item.item_number} className="flex items-center justify-between bg-white rounded p-2">
                          <div className="text-sm">
                            <strong>{item.item_number}</strong> - {item.name}
                          </div>
                          <Select
                            value={duplicateActions[item.item_number] || 'skip'}
                            onValueChange={(value) => 
                              setDuplicateActions({...duplicateActions, [item.item_number]: value})
                            }
                          >
                            <SelectTrigger className="w-48">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="skip">Überspringen</SelectItem>
                              <SelectItem value="overwrite">Überschreiben</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview Table */}
                <div className="border rounded-lg overflow-x-auto max-h-64">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left">Artikelnummer</th>
                        <th className="px-3 py-2 text-left">Bezeichnung</th>
                        <th className="px-3 py-2 text-left">Typ</th>
                        <th className="px-3 py-2 text-right">Preis</th>
                        <th className="px-3 py-2 text-left">Einheit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.slice(0, 10).map((item, index) => (
                        <tr key={index} className="border-b">
                          <td className="px-3 py-2 font-medium">{item.item_number}</td>
                          <td className="px-3 py-2">{item.name}</td>
                          <td className="px-3 py-2">
                            <Badge variant="secondary" className="text-xs">
                              {getTypeLabel(item.item_type)}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right">{formatPrice(item.unit_price)}</td>
                          <td className="px-3 py-2">{item.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importPreview.length > 10 && (
                    <div className="text-center py-2 text-sm text-slate-500 bg-slate-50">
                      ... und {importPreview.length - 10} weitere Artikel
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4 border-t">
              <Button
                onClick={() => {
                  setShowImportDialog(false);
                  setImportFile(null);
                  setImportPreview([]);
                  setDuplicates([]);
                  setDuplicateActions({});
                }}
                variant="outline"
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleImport}
                disabled={importPreview.length === 0}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importPreview.length} Artikel importieren
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

// Add Item Form Component
const AddItemForm = ({ formData, setFormData, onSubmit, categories, itemTypes }) => {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Artikelnummer *</label>
          <Input
            value={formData.item_number}
            onChange={(e) => setFormData({...formData, item_number: e.target.value})}
            placeholder="z.B. MAT-001"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Typ</label>
          <Select value={formData.item_type} onValueChange={(value) => setFormData({...formData, item_type: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {itemTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Bezeichnung *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          placeholder="z.B. Kabel NYM 3x1,5"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Beschreibung</label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          placeholder="Zusätzliche Details..."
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Einzelpreis (€) *</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={formData.unit_price}
            onChange={(e) => setFormData({...formData, unit_price: e.target.value})}
            placeholder="0.00"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Einheit</label>
          <Input
            value={formData.unit}
            onChange={(e) => setFormData({...formData, unit: e.target.value})}
            placeholder="Stück"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Kategorie</label>
          <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
            <SelectTrigger>
              <SelectValue placeholder="Wählen..." />
            </SelectTrigger>
            <SelectContent>
              {categories.map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Notizen</label>
        <Input
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Zusätzliche Informationen..."
        />
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
          Artikel erstellen
        </Button>
      </div>
    </form>
  );
};

// Price List Row Component (with inline editing)
const PriceListRow = ({
  item, index, isEditing, editValues, setEditValues,
  onEdit, onSaveEdit, onCancelEdit, onDelete,
  formatPrice, getTypeIcon, getTypeLabel, categories
}) => {
  if (isEditing) {
    return (
      <tr className={`border-b ${index % 2 === 0 ? 'bg-blue-50' : 'bg-blue-25'}`}>
        <td className="px-4 py-3">
          <span className="text-lg">{getTypeIcon(item.item_type)}</span>
        </td>
        <td className="px-4 py-3">
          <Input
            value={editValues.item_number}
            onChange={(e) => setEditValues({...editValues, item_number: e.target.value})}
            className="h-8 text-sm"
          />
        </td>
        <td className="px-4 py-3">
          <Input
            value={editValues.name}
            onChange={(e) => setEditValues({...editValues, name: e.target.value})}
            className="h-8 text-sm"
          />
        </td>
        <td className="px-4 py-3">
          <Select
            value={editValues.category}
            onValueChange={(value) => setEditValues({...editValues, category: value})}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="px-4 py-3">
          <Input
            type="number"
            step="0.01"
            min="0"
            value={editValues.unit_price}
            onChange={(e) => setEditValues({...editValues, unit_price: e.target.value})}
            className="h-8 text-sm w-24"
          />
        </td>
        <td className="px-4 py-3">
          <Input
            value={editValues.unit}
            onChange={(e) => setEditValues({...editValues, unit: e.target.value})}
            className="h-8 text-sm w-20"
          />
        </td>
        <td className="px-4 py-3">
          <div className="flex gap-1 justify-center">
            <Button 
              size="sm" 
              onClick={() => onSaveEdit(item.id)}
              className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700"
            >
              <Check className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={onCancelEdit}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`border-b hover:bg-slate-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-25'}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{getTypeIcon(item.item_type)}</span>
          <span className="text-xs text-slate-500">{getTypeLabel(item.item_type)}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="font-medium text-slate-900">{item.item_number}</span>
      </td>
      <td className="px-4 py-3">
        <div>
          <div className="font-medium text-slate-900">{item.name}</div>
          {item.description && (
            <div className="text-sm text-slate-500">{item.description}</div>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        {item.category && (
          <Badge variant="secondary" className="text-xs">
            {item.category}
          </Badge>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="font-medium text-emerald-600">
          {formatPrice(item.unit_price)}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-slate-600">{item.unit}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1 justify-center">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => onEdit(item)}
            className="h-8 w-8 p-0"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => onDelete(item.id, item.name)}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default PriceList;